import { Link } from '@tanstack/react-router';
import { ArrowRight, BookOpen, Check, ChevronRight, Compass, RefreshCw } from 'lucide-react';

import { AppShell } from '@/shared/layout/app-shell';
import { useContinueAt } from '@/shared/program/use-continue-at';
import { useStudio } from '@/shared/program/use-studio';
import { activityHref, ACTIVITY_KIND, formatMinutes } from '@/shared/ui/activity/activity-kind';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { buttonVariants } from '@/shared/ui/button-variants';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { PageSkeleton } from '@/shared/ui/skeleton';

import { useHomeSummary } from '../api/use-home-summary';

import './home-page.css';

export function HomePage() {
  const summary = useHomeSummary();
  const { data: studio, isPending, error, refetch, isFetching } = useStudio();
  const firstName = summary.data?.learner.displayName?.trim().split(/\s+/)[0];
  const next = useContinueAt(studio);
  // A selected Workshop can be in a different module from the first unfinished lesson.
  const activeModule = studio?.modules.find((module) => module.number === next?.moduleNumber);
  const modulePercent = activeModule?.items.length
    ? Math.round((activeModule.done / activeModule.items.length) * 100)
    : 0;
  const related =
    activeModule?.items
      .filter((item) => item.status !== 'complete' && item.id !== next?.id)
      .slice(0, 2) ?? [];
  const NextActivityIcon = next ? ACTIVITY_KIND[next.kind].icon : BookOpen;
  const complete = Boolean(studio && studio.total > 0 && studio.done === studio.total);

  return (
    <AppShell title="Overview">
      <div className="home-overview">
        <header className="home-heading">
          <h1>{firstName ? `Welcome back, ${firstName}.` : 'Your learning workspace'}</h1>
          <Link to="/studio" className="home-text-link">
            Open Studio <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </header>

        {error ? (
          <div className="home-load-error">
            <Alert>We could not load your overview. {error.message}</Alert>
            <Button variant="secondary" onClick={() => void refetch()} loading={isFetching}>
              <RefreshCw className="size-4" aria-hidden="true" /> Try again
            </Button>
          </div>
        ) : null}
        {isPending && !error ? <PageSkeleton label="Loading your learning overview…" /> : null}
        {studio ? (
          <>
            <section className="home-next" aria-labelledby="session-title">
              {next ? (
                <>
                  <div className="home-next-content">
                    <p className="home-label">
                      {next.status === 'complete'
                        ? 'Revisit your last activity'
                        : next.status === 'in_progress'
                          ? 'Pick up where you left off'
                          : 'Your next step'}
                    </p>
                    <p className="home-activity-context">
                      <NextActivityIcon className="size-4" aria-hidden="true" />
                      {ACTIVITY_KIND[next.kind].label}
                      <span aria-hidden="true">·</span> Module {next.moduleNumber}
                      {next.estimatedMinutes ? (
                        <>
                          <span aria-hidden="true">·</span> {formatMinutes(next.estimatedMinutes)}
                        </>
                      ) : null}
                    </p>
                    <h2 id="session-title">{next.title}</h2>
                    {next.summary ? <p className="home-next-description">{next.summary}</p> : null}
                    <Link
                      to={activityHref(next.kind, next.id)}
                      className={buttonVariants({ className: 'home-continue' })}
                    >
                      {next.status === 'complete'
                        ? 'Revisit'
                        : next.status === 'in_progress'
                          ? 'Continue'
                          : 'Start'}{' '}
                      {ACTIVITY_KIND[next.kind].label.toLowerCase()}
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                    {studio.path === null ? (
                      <div className="home-path-note">
                        <p>
                          Not sure where to start? A few questions point you to the right module.
                        </p>
                        <Link
                          to="/studio/path"
                          className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                        >
                          <Compass size={13} strokeWidth={1.75} aria-hidden="true" /> Choose your
                          path
                        </Link>
                      </div>
                    ) : null}
                  </div>
                  {activeModule ? (
                    <div className="home-module-progress" aria-label="Current module progress">
                      <div className="home-module-progress-copy">
                        <span>{activeModule.title}</span>
                        <span>
                          {activeModule.done} of {activeModule.items.length} activities complete
                        </span>
                      </div>
                      <ProgressBar
                        value={modulePercent}
                        label={`${activeModule.title} completion`}
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="home-next-content">
                  <p className="home-label">
                    {complete ? (
                      <>
                        <Check className="size-4" aria-hidden="true" /> Curriculum complete
                      </>
                    ) : (
                      'Your curriculum'
                    )}
                  </p>
                  <h2 id="session-title">
                    {complete
                      ? 'Ready to revisit what you’ve learned?'
                      : 'Your next activity will appear here.'}
                  </h2>
                  <p className="home-next-description">
                    {complete
                      ? `You’ve completed all ${studio.total} activities. Your lessons and workshops are still available in Studio.`
                      : 'Open Studio to see the lessons and workshops available to you.'}
                  </p>
                  <Link to="/studio" className={buttonVariants({ className: 'home-continue' })}>
                    {complete ? 'Revisit your work' : 'Browse Studio'}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </section>

            {activeModule && related.length > 0 ? (
              <section className="home-related" aria-labelledby="related-title">
                <div className="home-section-heading">
                  <h2 id="related-title">Also in this module</h2>
                  <Link
                    to="/studio/modules/$number"
                    params={{ number: String(activeModule.number) }}
                    className="home-text-link"
                  >
                    View module <ChevronRight className="size-4" aria-hidden="true" />
                  </Link>
                </div>
                <ul className="home-activity-list">
                  {related.map((item) => {
                    const Icon = ACTIVITY_KIND[item.kind].icon;
                    return (
                      <li key={item.id}>
                        <Link to={activityHref(item.kind, item.id)} className="home-activity-link">
                          <Icon className="size-[18px]" aria-hidden="true" />
                          <span className="home-activity-copy">
                            <span className="home-activity-title">{item.title}</span>
                            <span className="home-activity-kind">
                              {ACTIVITY_KIND[item.kind].label}
                            </span>
                          </span>
                          <ChevronRight className="size-4" aria-hidden="true" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
